import { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  Tabs,
  Row,
  Col,
  Statistic,
  Spin,
  Empty,
  Typography,
  Button,
  Select,
  Space,
} from 'antd';
import { DownloadOutlined, ReloadOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { dashboardApi } from '@tradermind/shared/api';

const { Title, Text } = Typography;

// 生成报告文本用于导出
function generateReportText(report: any, stats: any, t: any): string {
  const formatCurrency = (value: number) => `$${value.toFixed(2)}`;
  
  const lines = [
    `📊 ${report.title || t('export_text.report_title')}`,
    ``,
    t('export_text.stats_section'),
    `${t('export_text.total_trades')}: ${stats.total_trades} (${t('export_text.closed')}${stats.closed_trades}, ${t('export_text.open')}${stats.open_trades})`,
    `${t('export_text.net_pl')}: ${formatCurrency(stats.net_pl)}`,
    `${t('export_text.commission')}: ${formatCurrency(stats.total_commission)}`,
    `${t('export_text.win_rate')}: ${stats.win_rate?.toFixed(1)}%`,
    `${t('export_text.win_loss_ratio')}: ${stats.win_loss_ratio?.toFixed(2) || '-'}`,
    `${t('export_text.profit_factor')}: ${stats.profit_factor?.toFixed(2) || '-'}`,
    `${t('export_text.winning')}: ${stats.winning_trades}`,
    `${t('export_text.losing')}: ${stats.losing_trades}`,
    `${t('export_text.largest_win')}: ${formatCurrency(stats.largest_win)}`,
    `${t('export_text.largest_loss')}: ${formatCurrency(Math.abs(stats.largest_loss))}`,
    ``,
    t('export_text.psychology_section'),
    `${t('export_text.psychology_score')}: ${report.psychology_score}`,
    `${t('export_text.emotion_stability')}: ${report.emotion_stability}`,
    `${t('export_text.discipline')}: ${report.discipline_score}`,
    `${t('export_text.avg_emotion')}: ${report.avg_emotion_score?.toFixed(1) || '-'}/10`,
  ];

  if (report.psychology_insight) {
    lines.push(``, t('export_text.insight_section'), report.psychology_insight);
  }

  if (report.suggestions && report.suggestions.length > 0) {
    lines.push(``, t('export_text.suggestions_section'));
    report.suggestions.forEach((sug: any) => {
      lines.push(`• ${sug.title}: ${sug.description}`);
    });
  }

  lines.push(
    ``,
    `---`,
    `${t('export_text.generated_at')}: ${new Date(report.generated_at).toLocaleString()}`,
    `${t('export_text.report_engine')}: https://tradingmind.app`
  );

  return lines.join('\n');
}

// 获取当前年份和ISO周数
// ISO 8601: 周年由本周四所在的日历年决定
function getCurrentWeekInfo() {
  const now = new Date();
  // 计算本周四的日期（ISO周年由本周四决定）
  const dayOfWeek = now.getDay() || 7; // 周一=1, 周日=7
  const thursday = new Date(now);
  thursday.setDate(now.getDate() - dayOfWeek + 4);
  const year = thursday.getFullYear(); // ISO 周年
  
  // 计算该年第1周的周一
  const jan4 = new Date(year, 0, 4);
  const jan4DayOfWeek = jan4.getDay() || 7;
  const week1Monday = new Date(jan4);
  week1Monday.setDate(jan4.getDate() - jan4DayOfWeek + 1);
  
  // 计算周数
  const diff = thursday.getTime() - week1Monday.getTime();
  const week = Math.floor(diff / (7 * 24 * 60 * 60 * 1000)) + 1;
  return { year, week: Math.max(1, Math.min(week, 53)) };
}

export default function ReportsPage() {
  const { t } = useTranslation('reports');
  const [activeTab, setActiveTab] = useState('weekly');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const queryClient = useQueryClient();

  // 历史报告选择状态
  const currentWeekInfo = useMemo(() => getCurrentWeekInfo(), []);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedWeek, setSelectedWeek] = useState(currentWeekInfo.week);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYearForYearly, setSelectedYearForYearly] = useState(new Date().getFullYear());

  // 年份选项（2020-当前年）
  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let y = currentYear; y >= 2020; y--) {
      years.push({ value: y, label: `${y}${t('year_suffix')}` });
    }
    return years;
  }, [t]);

  // 周数选项（1-53）
  const weekOptions = useMemo(() => {
    const weeks = [];
    for (let w = 1; w <= 53; w++) {
      weeks.push({ value: w, label: `${t('week_prefix')}${w}${t('week_suffix')}` });
    }
    return weeks;
  }, [t]);

  // 月份选项
  const monthOptions = useMemo(() => {
    return [
      { value: 1, label: t('month.1') }, { value: 2, label: t('month.2') }, { value: 3, label: t('month.3') },
      { value: 4, label: t('month.4') }, { value: 5, label: t('month.5') }, { value: 6, label: t('month.6') },
      { value: 7, label: t('month.7') }, { value: 8, label: t('month.8') }, { value: 9, label: t('month.9') },
      { value: 10, label: t('month.10') }, { value: 11, label: t('month.11') }, { value: 12, label: t('month.12') },
    ];
  }, [t]);

  const { data: weeklyData, isLoading: isLoadingWeekly } = useQuery({
    queryKey: ['reports', 'weekly', selectedYear, selectedWeek],
    queryFn: () => dashboardApi.getWeeklyReport({ year: selectedYear, week: selectedWeek }),
    enabled: activeTab === 'weekly',
  });

  const { data: monthlyData, isLoading: isLoadingMonthly } = useQuery({
    queryKey: ['reports', 'monthly', selectedYear, selectedMonth],
    queryFn: () => dashboardApi.getMonthlyReport({ year: selectedYear, month: selectedMonth }),
    enabled: activeTab === 'monthly',
  });

  const { data: yearlyData, isLoading: isLoadingYearly } = useQuery({
    queryKey: ['reports', 'yearly', selectedYearForYearly],
    queryFn: () => dashboardApi.getYearlyReport({ year: selectedYearForYearly }),
    enabled: activeTab === 'yearly',
  });

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      if (activeTab === 'weekly') {
        const newData = await dashboardApi.getWeeklyReport({ year: selectedYear, week: selectedWeek, refresh: true });
        queryClient.setQueryData(['reports', 'weekly', selectedYear, selectedWeek], newData);
      } else if (activeTab === 'monthly') {
        const newData = await dashboardApi.getMonthlyReport({ year: selectedYear, month: selectedMonth, refresh: true });
        queryClient.setQueryData(['reports', 'monthly', selectedYear, selectedMonth], newData);
      } else if (activeTab === 'yearly') {
        const newData = await dashboardApi.getYearlyReport({ year: selectedYearForYearly, refresh: true });
        queryClient.setQueryData(['reports', 'yearly', selectedYearForYearly], newData);
      }
    } finally {
      setIsRefreshing(false);
    }
  };

  // 切换到上一期/下一期
  const handlePrev = () => {
    if (activeTab === 'weekly') {
      if (selectedWeek > 1) {
        setSelectedWeek(selectedWeek - 1);
      } else {
        setSelectedYear(selectedYear - 1);
        setSelectedWeek(52);
      }
    } else if (activeTab === 'monthly') {
      if (selectedMonth > 1) {
        setSelectedMonth(selectedMonth - 1);
      } else {
        setSelectedYear(selectedYear - 1);
        setSelectedMonth(12);
      }
    } else if (activeTab === 'yearly') {
      setSelectedYearForYearly(selectedYearForYearly - 1);
    }
  };

  const handleNext = () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;
    
    if (activeTab === 'weekly') {
      if (selectedYear < currentYear || (selectedYear === currentYear && selectedWeek < currentWeekInfo.week)) {
        if (selectedWeek < 52) {
          setSelectedWeek(selectedWeek + 1);
        } else {
          setSelectedYear(selectedYear + 1);
          setSelectedWeek(1);
        }
      }
    } else if (activeTab === 'monthly') {
      if (selectedYear < currentYear || (selectedYear === currentYear && selectedMonth < currentMonth)) {
        if (selectedMonth < 12) {
          setSelectedMonth(selectedMonth + 1);
        } else {
          setSelectedYear(selectedYear + 1);
          setSelectedMonth(1);
        }
      }
    } else if (activeTab === 'yearly') {
      if (selectedYearForYearly < currentYear) {
        setSelectedYearForYearly(selectedYearForYearly + 1);
      }
    }
  };

  // 检查是否可以前进（不能超过当前日期）
  const canGoNext = useMemo(() => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;
    
    if (activeTab === 'weekly') {
      return selectedYear < currentYear || (selectedYear === currentYear && selectedWeek < currentWeekInfo.week);
    } else if (activeTab === 'monthly') {
      return selectedYear < currentYear || (selectedYear === currentYear && selectedMonth < currentMonth);
    } else {
      return selectedYearForYearly < currentYear;
    }
  }, [activeTab, selectedYear, selectedWeek, selectedMonth, selectedYearForYearly, currentWeekInfo.week]);

  const report = activeTab === 'weekly' ? weeklyData?.data : activeTab === 'monthly' ? monthlyData?.data : yearlyData?.data;
  const isLoading = activeTab === 'weekly' ? isLoadingWeekly : activeTab === 'monthly' ? isLoadingMonthly : isLoadingYearly;

  // 后端直接在data中返回统计字段，不是嵌套在stats中
  const stats = {
    total_trades: report?.total_trades ?? 0,
    closed_trades: report?.closed_trades ?? 0,
    open_trades: report?.open_trades ?? 0,
    winning_trades: report?.winning_trades ?? 0,
    losing_trades: report?.losing_trades ?? 0,
    win_rate: report?.win_rate ?? 0,
    total_profit_loss: report?.total_profit_loss ?? 0,
    net_pl: report?.net_pl ?? 0,
    total_commission: report?.total_commission ?? 0,
    win_loss_ratio: report?.win_loss_ratio ?? 0,
    profit_factor: report?.profit_factor ?? 0,
    largest_win: report?.largest_win ?? 0,
    largest_loss: report?.largest_loss ?? 0,
    psychology_score: report?.psychology_score ?? 0,
    emotion_stability: report?.emotion_stability ?? 0,
    discipline_score: report?.discipline_score ?? 0,
  };

  const tabItems = [
    { key: 'weekly', label: t('weekly') },
    { key: 'monthly', label: t('monthly') },
    { key: 'yearly', label: t('yearly') },
  ];

  // 切换Tab时重置为当前日期
  const handleTabChange = (key: string) => {
    setActiveTab(key);
    const now = new Date();
    setSelectedYear(now.getFullYear());
    if (key === 'weekly') {
      setSelectedWeek(currentWeekInfo.week);
    } else if (key === 'monthly') {
      setSelectedMonth(now.getMonth() + 1);
    } else if (key === 'yearly') {
      setSelectedYearForYearly(now.getFullYear());
    }
  };

  // 导出报告
  const handleExport = () => {
    if (!report) return;

    const reportText = generateReportText(report, stats, t);
    
    // 创建 Blob 并下载
    const blob = new Blob([reportText], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${report.title || t('export_text.report_title')}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 100 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!report && !isLoading) {
    return <Empty description={t('no_data')} />;
  }

  return (
    <div>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={4} style={{ margin: 0 }}>
          {t('title')}
        </Title>
        <Space>
          <Button 
            icon={<ReloadOutlined />} 
            onClick={handleRefresh}
            loading={isRefreshing}
          >
            {t('regenerate')}
          </Button>
          <Button icon={<DownloadOutlined />} onClick={handleExport}>{t('export')}</Button>
        </Space>
      </div>

      <Tabs activeKey={activeTab} onChange={handleTabChange} items={tabItems} />

      {/* 历史报告选择器 */}
      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
        <Button icon={<LeftOutlined />} onClick={handlePrev} />
        {activeTab === 'yearly' ? (
          <Select
            value={selectedYearForYearly}
            onChange={setSelectedYearForYearly}
            options={yearOptions}
            style={{ width: 100 }}
          />
        ) : (
          <>
            <Select
              value={selectedYear}
              onChange={setSelectedYear}
              options={yearOptions}
              style={{ width: 100 }}
            />
            {activeTab === 'weekly' ? (
              <Select
                value={selectedWeek}
                onChange={setSelectedWeek}
                options={weekOptions}
                style={{ width: 100 }}
              />
            ) : (
              <Select
                value={selectedMonth}
                onChange={setSelectedMonth}
                options={monthOptions}
                style={{ width: 80 }}
              />
            )}
          </>
        )}
        <Button icon={<RightOutlined />} onClick={handleNext} disabled={!canGoNext} />
        <Text type="secondary" style={{ marginLeft: 8 }}>
          {report?.title || t('loading')}
        </Text>
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic 
              title={t('stats.total_trades')} 
              value={stats.total_trades} 
              suffix={t('stats.trades_suffix')}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic
              title={t('stats.winning_trades')}
              value={stats.winning_trades}
              valueStyle={{ color: '#10B981' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic
              title={t('stats.losing_trades')}
              value={stats.losing_trades}
              valueStyle={{ color: '#EF4444' }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} md={6}>
          <Card size="small">
            <Statistic
              title={t('stats.net_pl')}
              value={stats.net_pl}
              precision={2}
              prefix="$"
              valueStyle={{ color: stats.net_pl >= 0 ? '#10B981' : '#EF4444' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small">
            <Statistic
              title={t('stats.total_commission')}
              value={stats.total_commission}
              precision={2}
              prefix="$"
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small">
            <Statistic
              title={t('stats.largest_win')}
              value={stats.largest_win}
              precision={2}
              prefix="$"
              valueStyle={{ color: '#10B981' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small">
            <Statistic
              title={t('stats.largest_loss')}
              value={Math.abs(stats.largest_loss)}
              precision={2}
              prefix="-$"
              valueStyle={{ color: '#EF4444' }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic
              title={`${t('stats.win_rate')} (${stats.closed_trades}${t('stats.closed_trades')})`}
              value={stats.win_rate}
              precision={1}
              suffix="%"
              valueStyle={{ color: stats.win_rate >= 50 ? '#10B981' : '#EF4444' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic title={t('stats.win_loss_ratio')} value={stats.win_loss_ratio} precision={2} />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic
              title={t('stats.profit_factor')}
              value={stats.profit_factor}
              precision={2}
              valueStyle={{ color: stats.profit_factor >= 1 ? '#10B981' : '#EF4444' }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[24, 24]}>
        <Col xs={24} lg={12}>
          <Card title={t('emotion.title')}>
            <div>
              <div style={{ marginBottom: 16 }}>
                <Text type="secondary">{t('emotion.avg_score')}</Text>
                <div style={{ fontSize: 24, fontWeight: 600 }}>
                  {report?.avg_emotion_score?.toFixed(1) || 0}/10
                </div>
              </div>
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Text type="secondary">{t('emotion.stability')}</Text>
                  <div style={{ fontSize: 18, fontWeight: 500 }}>
                    {stats.emotion_stability}{t('emotion.score_suffix')}
                  </div>
                </Col>
                <Col span={12}>
                  <Text type="secondary">{t('emotion.psychology_score')}</Text>
                  <div style={{ fontSize: 18, fontWeight: 500 }}>
                    {stats.psychology_score}{t('emotion.score_suffix')}
                  </div>
                </Col>
              </Row>
            </div>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title={t('pattern.title')}>
            {report?.patterns && Array.isArray(report.patterns) && report.patterns.length > 0 ? (
              <div>
                {report.patterns.map((pattern: any, index: number) => (
                  <div key={pattern.id || index} style={{ marginBottom: 12 }}>
                    <Text strong>{pattern.name}</Text>
                    <div style={{ color: '#666', fontSize: 13 }}>{pattern.description}</div>
                    <div style={{ color: '#1890ff', fontSize: 12, marginTop: 4 }}>
                      💡 {pattern.suggestion}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Text type="secondary">{t('pattern.best_day')}</Text>
                  <div style={{ fontSize: 18, fontWeight: 500 }}>
                    {report?.best_trading_days?.[0] || '-'}
                  </div>
                </Col>
                <Col span={12}>
                  <Text type="secondary">{t('pattern.best_hour')}</Text>
                  <div style={{ fontSize: 18, fontWeight: 500 }}>
                    {report?.best_trading_hours?.[0] || '-'}
                  </div>
                </Col>
                <Col span={12}>
                  <Text type="secondary">{t('pattern.best_symbol')}</Text>
                  <div style={{ fontSize: 18, fontWeight: 500 }}>
                    {report?.best_symbols?.[0] || '-'}
                  </div>
                </Col>
                <Col span={12}>
                  <Text type="secondary">{t('pattern.discipline')}</Text>
                  <div style={{ fontSize: 18, fontWeight: 500 }}>
                    {stats.discipline_score}{t('emotion.score_suffix')}
                  </div>
                </Col>
              </Row>
            )}
          </Card>
        </Col>

        <Col xs={24}>
          <Card title={t('suggestions.title')}>
            {report?.suggestions && report.suggestions.length > 0 ? (
              <div>
                {report.suggestions.map((sug: any, index: number) => (
                  <div key={sug.id || index} style={{ marginBottom: 16, padding: 12, background: '#f5f5f5', borderRadius: 8 }}>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>{sug.title}</div>
                    <div style={{ color: '#666', marginBottom: 4 }}>{sug.description}</div>
                    <div style={{ color: '#1890ff' }}>📋 {sug.action}</div>
                    <div style={{ color: '#52c41a', fontSize: 12, marginTop: 4 }}>{t('suggestions.expected_impact')}: {sug.impact}</div>
                  </div>
                ))}
              </div>
            ) : (
              <Empty description={t('no_suggestions')} />
            )}
          </Card>
        </Col>

        {report?.psychology_insight && (
          <Col xs={24}>
            <Card title={t('psychology.title')}>
              <div style={{ whiteSpace: 'pre-line', lineHeight: 1.8 }}>
                {report.psychology_insight}
              </div>
            </Card>
          </Col>
        )}
      </Row>
    </div>
  );
}
